import { createFeatureSelector, createSelector, select } from '@ngrx/store';
import { combineLatest, map, Observable, of } from 'rxjs';

import { ErrorScannedCode, ScannedCodeType } from 'app/features/scan-flow/models';
import {
    getShortScannedCode,
    isActivityMatchLocationType,
    isActivitySupportedForScanFlow,
    parseScannedCode,
    scannedCodeLocationTypeTitle,
    scannedCodeTitle,
} from 'app/features/scan-flow/utils';
import { selectBatchByNumber } from 'app/shared/features/batch-selector/state/batch.selectors';
import { selectActivityByNumber } from 'app/state/activities/activity.selectors';
import { selectContainerByNumber } from 'app/state/containers/container.selectors';
import { selectEmployeeByNumber } from 'app/state/employee/employee.selectors';
import { selectLocationByNumber } from 'app/state/locations/locations.selectors';
import { selectScanFlowActivity } from 'app/state/scan-flow/scan-flow.selectors';

import { scannedCodesFeatureKey, ScannedCodeState } from './scanned-codes.reducer';

export const selectScannedCodesState = createFeatureSelector<ScannedCodeState>(scannedCodesFeatureKey);

export const selectScannedCodes = createSelector(selectScannedCodesState, (state) => state.scannedCodes);

export const selectScannedCodeByToken = (token: string) =>
    createSelector(selectScannedCodes, (state) => state[token]);

export const selectIsScannedCodeValid =
    ({ scannedCode, allowedCodeTypes }: { scannedCode: string; allowedCodeTypes: ScannedCodeType[] }) =>
    (store: Observable<object>): Observable<{ error: ErrorScannedCode | undefined }> => {
        if (!allowedCodeTypes.length) return of({ error: undefined });

        const parsedCode = parseScannedCode({ allowedCodeTypes, code: scannedCode });
        const { codeType, number, error } = parsedCode;
        if (!number || error) return of({ error });

        switch (codeType) {
            case 'employee':
                return store.pipe(
                    selectEmployeeByNumber(number),
                    map((employee) => {
                        if (!employee)
                            return {
                                error: {
                                    message: 'SCAN.ERROR.TITLE.UNKNOWN_CODE_SCANNED',
                                    scannedCodeType: scannedCodeTitle[codeType],
                                    shortScannedValue: getShortScannedCode(scannedCode),
                                },
                            };
                        return { error: undefined };
                    }),
                );
            case 'location':
                return combineLatest([
                    store.pipe(selectLocationByNumber(number)),
                    store.pipe(selectScanFlowActivity),
                ]).pipe(
                    map(([location, currentActivity]) => {
                        if (!location) {
                            return {
                                error: {
                                    message: 'SCAN.ERROR.TITLE.UNKNOWN_CODE_SCANNED',
                                    scannedCodeType: scannedCodeTitle[codeType],
                                    shortScannedValue: getShortScannedCode(scannedCode),
                                },
                            };
                        }

                        if (
                            currentActivity &&
                            !isActivityMatchLocationType(currentActivity, location.locationType)
                        ) {
                            const expectedlocationType =
                                currentActivity.locationType !== null
                                    ? scannedCodeLocationTypeTitle[currentActivity.locationType]
                                    : undefined;
                            const scannedLocationType =
                                scannedCodeLocationTypeTitle[location.locationType] ?? undefined;
                            return {
                                error: {
                                    message:
                                        'SCAN.ERROR.TITLE.LOCATION_TYPE_EXPECTED_BUT_OTHER_LOCATION_TYPE_SCANNED',
                                    expectedCodeType: expectedlocationType,
                                    scannedCodeType: scannedLocationType,
                                },
                            };
                        }
                        return { error: undefined };
                    }),
                );
            case 'container':
                return store.pipe(
                    select(selectContainerByNumber(number)),
                    map((container) => ({
                        error: {
                            message: !container ? 'SCAN.ERROR.TITLE.UNKNOWN_CODE_SCANNED' : undefined,
                            scannedCodeType: scannedCodeTitle[codeType],
                            shortScannedValue: getShortScannedCode(scannedCode),
                        },
                    })),
                );
            case 'activity': {
                return store.pipe(
                    selectActivityByNumber(number),
                    map((activity) => {
                        if (!activity) {
                            return {
                                error: {
                                    message: 'SCAN.ERROR.TITLE.UNKNOWN_CODE_SCANNED',
                                    scannedCodeType: scannedCodeTitle[codeType],
                                    shortScannedValue: getShortScannedCode(scannedCode),
                                },
                            };
                        } else if (!isActivitySupportedForScanFlow(activity)) {
                            return {
                                error: {
                                    message: `SCAN.ERROR.TITLE.ACTIVITY_NOT_SUPPORTED`,
                                    shortScannedValue: activity.name,
                                },
                            };
                        }
                        return { error: undefined };
                    }),
                );
            }
            case 'batch': {
                return store.pipe(
                    selectBatchByNumber(number),
                    map((batch) => ({
                        error: {
                            message: !batch ? 'SCAN.ERROR.TITLE.UNKNOWN_CODE_SCANNED' : undefined,
                            scannedCodeType: scannedCodeTitle[codeType],
                            shortScannedValue: getShortScannedCode(scannedCode),
                        },
                    })),
                );
            }
            default:
                return of({ error: undefined });
        }
    };
